import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { SmartLinkStatsTotals } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../common/types';
import { Panel } from '../../music24/components/primitives/Panel';
import { mediaStatsMessages } from './media/mediaStatsMessages';
import styles from './SMLStatCards.module.scss';

interface SMLStatCardsProps {
  totals: SmartLinkStatsTotals;
  /**
   * The campaign's own count, on a page that has one. The analytics pipeline
   * counts pre-save events; the campaign counts subscriptions, and the
   * subscriptions are what the artist is owed on release day.
   */
  presaveCount?: number;
  /** A pre-save page measures a different conversion from a link page. */
  hasCampaign?: boolean;
  /**
   * Shows the "Video plays" card (design c1): the page has a video Media block, or the window has
   * plays. `watchedPct` is the average watched share of the plays, 0-100, or null.
   */
  videoPlays?: { watchedPct: number | null };
}

const SMLStatCardsComponent: React.FC<SMLStatCardsProps> = ({
  totals,
  presaveCount,
  hasCampaign = false,
  videoPlays,
}) => {
  const intl = useIntl();
  const presaves = presaveCount ?? totals.presaves;

  const cards = [
    {
      label: intl.formatMessage({ id: 'app.sml.stats.views', defaultMessage: 'Page views' }),
      value: intl.formatNumber(totals.views, { notation: 'compact' }),
      isZero: totals.views === 0,
      hint:
        totals.views === 0
          ? intl.formatMessage({ id: 'app.sml.stats.visitors.none', defaultMessage: 'nobody yet' })
          : intl.formatMessage(
              { id: 'app.sml.stats.visitors', defaultMessage: '{count} people' },
              { count: intl.formatNumber(totals.visitors, { notation: 'compact' }) }
            ),
    },
    {
      label: intl.formatMessage({ id: 'app.sml.stats.clicks', defaultMessage: 'Link clicks' }),
      value: intl.formatNumber(totals.clicks, { notation: 'compact' }),
      isZero: totals.clicks === 0,
      hint: intl.formatMessage({ id: 'app.sml.stats.clicks.hint', defaultMessage: 'routed to a service' }),
    },
    {
      label: hasCampaign
        ? intl.formatMessage({ id: 'app.sml.stats.conversion', defaultMessage: 'Conversion' })
        : intl.formatMessage({ id: 'app.sml.stats.ctr', defaultMessage: 'Click-through' }),
      // Null, not zero: no views means the rate is unknown, and 0% would read
      // as "nobody clicked".
      value:
        totals.clickThroughRate === null
          ? '—'
          : intl.formatNumber(totals.clickThroughRate / 100, { style: 'percent', maximumFractionDigits: 1 }),
      isZero: totals.clickThroughRate === null || totals.clickThroughRate === 0,
      hint: hasCampaign
        ? intl.formatMessage({ id: 'app.sml.stats.conversion.hint', defaultMessage: 'views that pre-saved' })
        : intl.formatMessage({ id: 'app.sml.stats.ctr.hint', defaultMessage: 'views that acted' }),
    },
    {
      label: intl.formatMessage({ id: 'app.sml.stats.presaves', defaultMessage: 'Pre-saves' }),
      // A 0 is a real number (owner rule 2026-10-18): it shows as a grey 0, not a dash.
      value: intl.formatNumber(presaves, { notation: 'compact' }),
      isZero: presaves === 0,
      hint: intl.formatMessage({ id: 'app.sml.stats.presaves.hint', defaultMessage: 'queued for release day' }),
    },
    ...(videoPlays === undefined
      ? []
      : [
          {
            label: intl.formatMessage(mediaStatsMessages.playsCard),
            // An old server sends no count: a dash, never 0.
            value:
              totals.mediaPlays === undefined || totals.mediaPlays === null
                ? '—'
                : intl.formatNumber(totals.mediaPlays, { notation: 'compact' }),
            isZero: totals.mediaPlays === undefined || totals.mediaPlays === null || totals.mediaPlays === 0,
            hint:
              videoPlays.watchedPct === null
                ? intl.formatMessage(mediaStatsMessages.playsCardHintNone)
                : intl.formatMessage(mediaStatsMessages.playsCardHint, {
                    percent: intl.formatNumber(videoPlays.watchedPct / 100, {
                      style: 'percent',
                      maximumFractionDigits: 0,
                    }),
                  }),
          },
        ]),
  ];

  return (
    <div className={styles.grid}>
      {cards.map((card) => (
        <Panel key={card.label} className={styles.card}>
          <div className={styles.label}>{card.label}</div>
          {/* A grey zero, so the numbers that are real stand out next to it. */}
          <div className={clsx(styles.value, card.isZero && styles.zero)}>{card.value}</div>
          <div className={styles.hint}>{card.hint}</div>
        </Panel>
      ))}
    </div>
  );
};

export const SMLStatCards = TypedMemo(SMLStatCardsComponent);
